import React, { useEffect, useState, useMemo } from 'react';
import { Plus, Users, Search, MessageSquare } from 'lucide-react';
import { useChatContext } from 'stream-chat-react';
import { useStreamChat } from '../../../context/StreamChatContext';
import CustomChannelListItem from './CustomChannelListItem';
import styles from '../Chat.module.css';

const CustomChannelList = ({ organizationId, onChannelSelect, isMobile, onCreateChannel }) => {
  const { client } = useChatContext();
  const { refreshUserPresence } = useStreamChat();
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [unreadCount, setUnreadCount] = useState(0);

  const filterTabs = [
    { id: 'all', label: 'All' },
    { id: 'unread', label: 'Unread', badge: unreadCount > 0 ? unreadCount : null },
    { id: 'teams', label: 'Teams' },
  ];

  useEffect(() => {
    if (!client) return;

    const fetchChannels = async () => {
      try {
        const filter = {
          type: 'messaging',
          members: { $in: [client.user?.id] }
        };
        const sort = { last_message_at: -1 };

        const response = await client.queryChannels(filter, sort, { limit: 50 });
        setChannels(response);

        let totalUnread = 0;
        response.forEach(channel => {
          totalUnread += channel.state.unreadCount || 0;
        });
        setUnreadCount(totalUnread);

        const userIds = new Set();
        response.forEach(channel => {
          Object.values(channel.state.members || {}).forEach(member => {
            if (member.user?.id && member.user.id !== client.user?.id) {
              userIds.add(member.user.id);
            }
          });
        });

        if (userIds.size > 0 && refreshUserPresence) {
          await refreshUserPresence(Array.from(userIds));
        }
      } catch (error) {
        console.error('Error fetching channels:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChannels();

    const handleEvent = () => { fetchChannels(); };

    client.on('channel.updated', handleEvent);
    client.on('channel.deleted', handleEvent);
    client.on('message.new', handleEvent);
    client.on('notification.message_new', handleEvent);
    client.on('notification.mark_read', handleEvent);

    return () => {
      client.off('channel.updated', handleEvent);
      client.off('channel.deleted', handleEvent);
      client.off('message.new', handleEvent);
      client.off('notification.message_new', handleEvent);
      client.off('notification.mark_read', handleEvent);
    };
  }, [client, refreshUserPresence]);

  const filteredChannels = useMemo(() => {
    let filtered = [...channels];

    if (activeFilter === 'unread') {
      filtered = filtered.filter(channel => (channel.state.unreadCount || 0) > 0);
    } else if (activeFilter === 'teams') {
      filtered = filtered.filter(channel => Object.keys(channel.state.members || {}).length > 2);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(channel => {
        if (channel.data?.name?.toLowerCase().includes(query)) return true;
        const members = Object.values(channel.state.members || {});
        return members.some(member => {
          const userName = member.user?.name || member.user?.id || '';
          return userName.toLowerCase().includes(query);
        });
      });
    }

    return filtered;
  }, [channels, activeFilter, searchQuery]);

  const handleChannelClick = (channel) => {
    setActiveChannel(channel);
    onChannelSelect(channel);
  };

  const getChannelName = (channel) => {
    if (channel.data?.name) return channel.data.name;

    const members = Object.values(channel.state.members || {});
    const otherMembers = members.filter(m => m.user?.id !== client.user?.id);

    if (otherMembers.length === 1) {
      return otherMembers[0].user?.name || otherMembers[0].user?.id || 'Unknown User';
    } else if (otherMembers.length > 1) {
      return otherMembers.map(m => m.user?.name || m.user?.id).join(', ');
    }

    return 'New Conversation';
  };

  return (
    <div className={`${styles.sidebar} ${isMobile ? styles.mobile : ''}`}>
      {/* Header */}
      <div className={styles.sidebarHeader}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--sp-3)' }}>
          <div>
            <h3 className={styles.sidebarTitle}>Messages</h3>
            <p className={styles.sidebarSubtitle}>Team chat</p>
          </div>
          <button onClick={onCreateChannel} className={styles.newChatBtn} title="Start new conversation">
            <Plus size={18} />
          </button>
        </div>

        {/* Search Bar */}
        <div className={styles.searchWrap}>
          <Search size={16} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        {/* Filter Tabs */}
        <div className={styles.filterTabs}>
          {filterTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveFilter(tab.id)}
              className={`${styles.filterTab} ${activeFilter === tab.id ? styles.active : ''}`}
            >
              {tab.label}
              {tab.badge && (
                <span
                  className={styles.filterBadge}
                  style={{
                    background: activeFilter === tab.id ? 'white' : 'var(--lodge-orange-400)',
                    color: activeFilter === tab.id ? 'var(--lodge-orange-400)' : 'white',
                  }}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Channel List */}
      <div className={styles.channelList}>
        {loading ? (
          <div className={styles.emptyState}>
            <div className={styles.spinner} />
            <p style={{ marginTop: 'var(--sp-4)', color: 'var(--lodge-onyx-400)', fontSize: 13 }}>
              Loading conversations...
            </p>
          </div>
        ) : filteredChannels.length === 0 ? (
          <div className={styles.emptyState}>
            {searchQuery ? (
              <>
                <div className={styles.emptyIcon}><Search size={28} /></div>
                <h4 className={styles.emptyTitle}>No results found</h4>
                <p className={styles.emptyText}>Try searching with different keywords</p>
              </>
            ) : activeFilter === 'unread' ? (
              <>
                <div className={styles.emptyIcon}><MessageSquare size={28} /></div>
                <h4 className={styles.emptyTitle}>All caught up!</h4>
                <p className={styles.emptyText}>No unread messages</p>
              </>
            ) : (
              <>
                <div className={styles.emptyIcon}><Users size={28} /></div>
                <h4 className={styles.emptyTitle}>No conversations yet</h4>
                <p className={styles.emptyText}>Start chatting with your team members</p>
                <button onClick={onCreateChannel} className={styles.btnPrimary} style={{ marginTop: 'var(--sp-4)' }}>
                  Start conversation
                </button>
              </>
            )}
          </div>
        ) : (
          filteredChannels.map((channel) => (
            <CustomChannelListItem
              key={channel.id}
              channel={channel}
              isActive={activeChannel?.id === channel.id}
              onClick={() => handleChannelClick(channel)}
              channelName={getChannelName(channel)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default CustomChannelList;
